import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import background from "./images/background.png";
import { toast } from "react-toastify";

const toneOptions = [
  { value: "informativo", label: "Al grano, bb" },
  { value: "chistoso", label: "Con sazón 🌶️" },
  { value: "emocional", label: "Desde el corazón 🩷" },
];

// ── SessionStorage helpers ────────────────────────────────────────────────────
const SESSION_KEY = "kk_tweet_batch";

const loadSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveSession = (tone, batch, index) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ tone, batch, index }));
  } catch {}
};

const clearSession = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
};

export default function GenerateTweet() {
  const navigate = useNavigate();
  // Restore from sessionStorage on mount
  const session = loadSession();
  const [tone, setTone] = useState(session?.tone || "informativo");
  const [phrase, setPhrase] = useState("");
  const [tweet, setTweet] = useState(
    session ? session.batch[session.index - 1] || "" : ""
  );
  const [tweetBatch, setTweetBatch] = useState(session?.batch || []);
  const [batchIndex, setBatchIndex] = useState(session?.index || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const hasShownToast = useRef(false);

  // Reset batch when tone changes (but not on mount)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setTweetBatch([]);
    setBatchIndex(0);
    setTweet("");
    clearSession();
  }, [tone]);

  const handleGenerate = async () => {
    setError("");
    setCopied(false);
    if (tweetBatch.length > 0 && batchIndex < tweetBatch.length) {
      setTweet(tweetBatch[batchIndex]);
      setBatchIndex((i) => {
        saveSession(tone, tweetBatch, i + 1);
        return i + 1;
      });
      return;
    }
    setLoading(true);
    setTweet("");
    try {
      const res = await fetch("/api/generate-tweet", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phrase,
          tone,
          batchSize: 20,
          refresh: tweetBatch.length > 0 && batchIndex >= tweetBatch.length,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error generando tuits");
      const tweets = Array.isArray(data.tweets)
        ? data.tweets.filter(Boolean)
        : [];
      if (!tweets.length) throw new Error("No se generaron tuits");
      setTweetBatch(tweets);
      setBatchIndex(1);
      setTweet(tweets[0]);
      saveSession(tone, tweets, 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyTweet = () => {
    if (!tweet) return;
    navigator.clipboard.writeText(tweet);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const openTwitter = () => {
    if (!tweet?.trim()) return;
    const text = encodeURIComponent(tweet);
    const appUrls = [
      `twitter://post?message=${text}`,
      `x://post?message=${text}`,
    ];
    const webUrl = `https://twitter.com/intent/tweet?text=${text}`;
    const popup = window.open("", "_blank");
    if (!popup) {
      window.open(webUrl, "_blank");
      return;
    }
    let i = 0;
    const tryNext = () => {
      if (i < appUrls.length) {
        popup.location.href = appUrls[i++];
        setTimeout(tryNext, 900);
      } else {
        popup.location.href = webUrl;
      }
    };
    tryNext();
  };

  useEffect(() => {
    const fetchPhrase = async () => {
      try {
        const res = await fetch("/data/trending.json");
        const data = await res.json();
        const currentTrend = data.find((item) => item.type === "trend");
        const now = new Date();
        if (
          currentTrend?.phrase &&
          currentTrend?.startTime &&
          currentTrend?.endTime
        ) {
          const start = new Date(currentTrend.startTime);
          const end = new Date(currentTrend.endTime);
          if (now >= start && now <= end) {
            setPhrase(currentTrend.phrase);
            return;
          }
        }
        if (!hasShownToast.current) {
          toast.info("No hay tendencia activa, bb. Te avisamos cuando haya ✨");
          hasShownToast.current = true;
          navigate("/tendencia", { replace: true });
        }
      } catch (err) {
        console.error("Error cargando trending.json:", err);
        if (!hasShownToast.current) {
          toast.error("Hubo un error cargando la tendencia");
          hasShownToast.current = true;
          navigate("/tendencia", { replace: true });
        }
      }
    };
    fetchPhrase();
  }, [navigate]);

  return (
    <div
      className="min-h-full flex items-center justify-center px-6 py-16 font-quicksand"
      style={{
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div
        className="w-full max-w-2xl px-5 py-6 text-center flex flex-col gap-6"
        style={{
          background: "rgba(255,255,255,0.92)",
          borderRadius: "20px",
          border: "2px solid #f9a8d4",
          boxShadow: "0 8px 32px rgba(176,106,179,0.12)",
        }}
      >
        <h1 className="text-xl font-bold" style={{ color: "#b06ab3" }}>
          Arma tu tuit perrísimo para la tendencia 🐩✨
        </h1>

        {/* Active phrase reminder */}
        {phrase && (
          <div
            className="px-4 py-3 rounded-2xl text-center"
            style={{
              background: "rgba(176,106,179,0.08)",
              border: "1.5px solid rgba(176,106,179,0.2)",
            }}
          >
            <p
              className="text-xs font-bold uppercase tracking-wider mb-1"
              style={{ color: "#b06ab3" }}
            >
              Lo que vamos a hacer tendencia ✨
            </p>
            <p className="font-extrabold text-sm" style={{ color: "#2d1f3d" }}>
              {phrase}
            </p>
          </div>
        )}

        {/* Tone selector */}
        <div>
          <p
            className="font-semibold mb-4 text-sm"
            style={{ color: "#6b7280" }}
          >
            Elige tu tono, bb
          </p>
          <div className="flex justify-center gap-2 flex-wrap">
            {toneOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTone(opt.value)}
                className="px-4 py-1.5 rounded-full font-semibold transition-all text-sm"
                style={{
                  background:
                    tone === opt.value ? "#7BDCFB" : "rgba(196,181,253,0.15)",
                  color: tone === opt.value ? "#0c4a6e" : "#9b7eaa",
                  border:
                    tone === opt.value
                      ? "2px solid #38bdf8"
                      : "1.5px solid #c4b5fd",
                  boxShadow:
                    tone === opt.value
                      ? "0 4px 12px rgba(125,211,252,0.3)"
                      : "none",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Generate button */}
        <button
          onClick={handleGenerate}
          disabled={loading || !phrase.trim()}
          className="w-full py-3 rounded-full font-bold active:scale-95 transition text-white"
          style={{
            background: loading || !phrase.trim() ? "#e2e8f0" : "#7BDCFB",
            color: loading || !phrase.trim() ? "#9ca3af" : "#0c4a6e",
            boxShadow:
              loading || !phrase.trim()
                ? "none"
                : "0 4px 12px rgba(125,211,252,0.4)",
          }}
        >
          {loading
            ? "Generando..."
            : tweetBatch.length && batchIndex < tweetBatch.length
            ? "Otro tuit"
            : "Generar tuit"}
        </button>

        {error && (
          <p className="text-sm" style={{ color: "#f472b6" }}>
            {error}
          </p>
        )}

        {/* Tweet result */}
        {tweet && (
          <div className="space-y-4">
            <div
              className="p-4 rounded-2xl whitespace-pre-wrap text-sm text-left"
              style={{
                background: "rgba(196,181,253,0.1)",
                border: "1.5px solid #c4b5fd",
                color: "#2d1f3d",
              }}
            >
              {tweet}
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={copyTweet}
                className="px-5 py-2 rounded-full font-semibold text-sm active:scale-95 transition"
                style={{
                  background: "#7BDCFB",
                  color: "#0c4a6e",
                  boxShadow: "0 4px 12px rgba(125,211,252,0.3)",
                }}
              >
                Copiar
              </button>
              <button
                onClick={openTwitter}
                className="px-5 py-2 rounded-full font-semibold text-sm active:scale-95 transition"
                style={{
                  border: "1.5px solid #b06ab3",
                  color: "#b06ab3",
                  background: "rgba(176,106,179,0.08)",
                }}
              >
                Abrir X
              </button>
            </div>
            <div
              className={`h-5 text-xs font-semibold transition-opacity duration-300 ${
                copied ? "opacity-100" : "opacity-0"
              }`}
              style={{ color: "#b06ab3" }}
            >
              ¡Copiado!
            </div>
          </div>
        )}

        <a
          href="/tendencia"
          className="text-sm underline"
          style={{ color: "#9b7eaa" }}
        >
          ← Volver a la tendencia
        </a>
      </div>
    </div>
  );
}
